// src/services/businessOnboardingApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { 
  OMNISTACK_BASE_URL, 
  getPrepareHeaders 
} from '@/hooks/common/common-functions'

export const businessOnboardingApi = createApi({
  reducerPath: 'businessOnboardingApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${OMNISTACK_BASE_URL}business/onboarding`,
    prepareHeaders: (headers) => {
      // Add standard headers
      getPrepareHeaders({
        headers,
        isClientApiKey: true,
      });

      // Add business-specific API key
      const businessApiKey = localStorage.getItem('businessApiKey');
      if (businessApiKey) {
        headers.set('business-x-api-key', businessApiKey);
      }

      return headers;
    },
  }),
  tagTypes: ['BusinessOnboarding', 'OnboardingAnalytics', 'NotificationPreferences'],
  endpoints: (builder) => ({
    // Initialize business onboarding
    initializeOnboarding: builder.mutation({
      query: (onboardingData) => ({
        url: '',
        method: 'POST',
        body: onboardingData
      }),
      invalidatesTags: ['BusinessOnboarding', 'OnboardingAnalytics']
    }),

    // Get onboarding progress
    getOnboardingProgress: builder.query({
      query: ({ businessId, type }) => ({
        url: '',
        params: {
          businessId,
          type
        }
      }),
      providesTags: ['BusinessOnboarding']
    }),

    // Get single onboarding record
    getOnboarding: builder.query({
      query: ({ id, businessId }) => ({
        url: `/${id}`,
        params: { businessId }
      }),
      //@ts-ignore
      providesTags: (result, error, { id }) => [{ type: 'BusinessOnboarding', id }]
    }),

    // Update onboarding progress
    updateOnboarding: builder.mutation({
      query: ({ id, ...updateData }) => ({
        url: `/${id}`,
        method: 'PUT',
        body: updateData
      }),
      invalidatesTags: ['BusinessOnboarding', 'OnboardingAnalytics']
    }),

    // Get onboarding analytics
    getOnboardingAnalytics: builder.query({
      query: (businessId) => ({
        url: '/analytics',
        params: { businessId }
      }),
      providesTags: ['OnboardingAnalytics']
    }),

    // Mark step as complete
    markStepComplete: builder.mutation({
      query: ({ businessId, type, step }) => ({
        url: '/step/complete',
        method: 'POST',
        body: {
          businessId,
          type,
          step
        }
      }),
      invalidatesTags: ['BusinessOnboarding', 'OnboardingAnalytics']
    }),

    // Complete onboarding
    completeOnboarding: builder.mutation({
      query: ({ businessId, type }) => ({
        url: '/complete',
        method: 'POST',
        body: {
          businessId,
          type
        }
      }),
      invalidatesTags: ['BusinessOnboarding', 'OnboardingAnalytics']
    }),

    // Dismiss onboarding
    dismissOnboarding: builder.mutation({
      query: ({ businessId, type, reason }) => ({
        url: '/dismiss',
        method: 'POST',
        body: {
          businessId,
          type,
          reason
        }
      }),
      invalidatesTags: ['BusinessOnboarding', 'OnboardingAnalytics']
    }),

    // Get incomplete onboarding (admin endpoint)
    getIncompleteOnboarding: builder.query({
      query: ({ days = 7 }) => ({
        url: '/incomplete',
        params: { days }
      }),
      providesTags: ['OnboardingAnalytics']
    }),

    // Get onboarding state (combined walkthrough + setup guide)
    getOnboardingState: builder.query({
      query: (businessId) => ({
        url: '/state',
        params: { businessId }
      }),
      providesTags: ['BusinessOnboarding']
    }),

    // Get completion stats for dashboard
    getCompletionStats: builder.query({
      query: (businessId) => ({
        url: '/stats/completion',
        params: { businessId }
      }),
      providesTags: ['OnboardingAnalytics']
    }),

    // Check if onboarding is required
    checkOnboardingRequired: builder.query({
      query: ({ businessId, type }) => ({
        url: '/check-required',
        params: {
          businessId,
          type
        }
      }),
      providesTags: ['BusinessOnboarding']
    }),

    // Reset onboarding (start over)
    resetOnboarding: builder.mutation({
      query: ({ businessId, type }) => ({
        url: '/reset',
        method: 'POST',
        body: {
          businessId,
          type
        }
      }),
      invalidatesTags: ['BusinessOnboarding', 'OnboardingAnalytics']
    }),

    // Update onboarding metadata
    updateOnboardingMetadata: builder.mutation({
      query: ({ businessId, type, metadata }) => ({
        url: '/metadata',
        method: 'PUT',
        body: {
          businessId,
          type,
          metadata
        }
      }),
      invalidatesTags: ['BusinessOnboarding']
    }),

    // Get onboarding timeline (history)
    getOnboardingTimeline: builder.query({
      query: (businessId) => ({
        url: '/timeline',
        params: { businessId }
      }),
      providesTags: ['OnboardingAnalytics']
    }),

    updateNotificationPreferences: builder.mutation({
      query: ({ businessId, ...updateData }) => ({
        url: '/notification-preferences',
        method: 'PUT',
        params: { businessId },
        body: updateData
      }),
      // ✅ Ensure proper cache invalidation
      // @ts-ignore
      invalidatesTags: (result, error, { businessId }) => [
        { type: 'NotificationPreferences', id: businessId },
        'NotificationPreferences' // Invalidate all notification preferences
      ],
      // ✅ Optimistic update option
      async onQueryStarted({ businessId, ...updateData }, { dispatch, queryFulfilled }) {
        // Optimistically update the cache
        const patchResult = dispatch(
          businessOnboardingApi.util.updateQueryData(
            'getNotificationPreferences',
            businessId,
            (draft) => {
              if (updateData.emailNotificationsEnabled !== undefined) {
                draft.emailNotificationsEnabled = updateData.emailNotificationsEnabled;
              }
              if (updateData.smsNotificationsEnabled !== undefined) {
                draft.smsNotificationsEnabled = updateData.smsNotificationsEnabled;
              }
            }
          )
        );
    
        try {
          await queryFulfilled;
        } catch {
          // Undo the optimistic update if the mutation fails
          patchResult.undo();
        }
      }
    }),
    
    // Get notification preferences for a user
    getNotificationPreferences: builder.query({
      query: (businessId) => ({
        url: '/notification-preferences',
        params: { businessId }
      }),
      // ✅ Ensure proper cache tagging
           // @ts-ignore
      providesTags: (result, error, businessId) => [
        { type: 'NotificationPreferences', id: businessId },
        'NotificationPreferences'
      ]
    }),
  })
});

export const {
  useInitializeOnboardingMutation,
  useGetOnboardingProgressQuery,
  useGetOnboardingQuery,
  useUpdateOnboardingMutation,
  useGetOnboardingAnalyticsQuery,
  useMarkStepCompleteMutation,
  useCompleteOnboardingMutation,
  useDismissOnboardingMutation,
  useGetIncompleteOnboardingQuery,
  useGetOnboardingStateQuery,
  useGetCompletionStatsQuery,
  useCheckOnboardingRequiredQuery,
  useResetOnboardingMutation,
  useUpdateOnboardingMetadataMutation,
  useGetOnboardingTimelineQuery,
  useUpdateNotificationPreferencesMutation,
  useGetNotificationPreferencesQuery,
} = businessOnboardingApi;